import React, { useState } from "react";
import { Row, Col, Button, Form, notification } from "antd";
import CustomButton from "../../common/Buttons/CustomButton";
import PlanCard from "../Shared/PlanCard";
import {
  GeneralDefinition,
  InsertParameter,
  ServicesTypesComponentLoad,
  TrackAnalytics,
} from "../../types/ApplicationTypes";
import styles from "./WandaPlusSubscription.module.scss";
import CreditCardForm from "../Shared/CreditCardForm/CreditCardForm";
import { backArrowIcon } from "../../assets/images";
import { useCreditCardActions } from "../../hooks/useCreditCardActions";
import { useTripsCtx } from "../../context/TripDetailsContext";
import { UpdateCCPRegistrationDetails } from "../../services/RegistrationService";
import { usePlanDetailsCtx } from "../../context/PlanDetailsContext";
import emailWandaPlusSubscription from "../../helpers/emailWandaPlusSubscription";
import { useSubscriptionActions } from "../../hooks/useSubscriptionActions";
import { usePersonSummaryCtx } from "../../context/PersonSummaryContext";
import { useServiceUserCreationCtx } from "../../context/ServiceUserCreationContext";

type Props = {
  setPageType: Function;
};

function WandaPlusSubscription({ setPageType }: Props) {
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [upgradePlan, setUpgradePlan] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [form] = Form.useForm();
  const {
    saveCreditCard,
    isValidCCState,
    resetFormAndSetDefaults,
    shouldDisableButton,
    validateAndCheckForm,
  } = useCreditCardActions(form);

  const { subscribeToWandaPlus } = useSubscriptionActions();
  const { caregiverSummaryInfo } = usePersonSummaryCtx();
  const { whoWillUseServicesCtx } = useServiceUserCreationCtx();
  const [whoWillUseServices] = whoWillUseServicesCtx;

  const handleCardClick = (plan: string) => setSelectedPlan(plan);
  const { wandaOrgId } = useTripsCtx();
  const {
    tripDetailsInfo,
    getCurrentSubscriptionPlanDetails,
  } = usePlanDetailsCtx();

  const handleContinue = async () => {
    if (selectedPlan === "Basic") {
      await Promise.all([
        handleUpdateCCPRegistrationDetails(),
        getCurrentSubscriptionPlanDetails(),
      ]).catch(() => {
        notification.error({
          message: "Error",
          description: "An error occurred during updating the details",
        });
      });
      setPageType(ServicesTypesComponentLoad.DashBoard);
      TrackAnalytics(
        "/Registration",
        "Registration subscription selection",
        "Registration Plan Modal"
      );
    }
    selectedPlan === "Plus" && setUpgradePlan(true);
  };

  const handleBack = () => {
    !upgradePlan &&
      setPageType(ServicesTypesComponentLoad.AddAnotherServiceUser);
    setSelectedPlan("");
    setUpgradePlan(false);
    resetFormAndSetDefaults();
  };

  const handleUpgradePlus = async () => {
    try {
      setIsLoading(true);
      const { paymentInstrumentId } = await saveCreditCard(true);

      await subscribeToWandaPlus(paymentInstrumentId);
      // set time out for phase-1, since its taking time to process transaction(handled in backend)
      await new Promise((resolve) => setTimeout(resolve, 3000));

      localStorage.setItem("showModal", "true");
      resetFormAndSetDefaults();

      const [{ renewOn }] = await getCurrentSubscriptionPlanDetails();

      setPageType(ServicesTypesComponentLoad.DashBoard);

      Promise.all([
        handleUpdateCCPRegistrationDetails(),
        emailWandaPlusSubscription(
          caregiverSummaryInfo.personId,
          caregiverSummaryInfo.firstName,
          wandaOrgId.privatePayId,
          tripDetailsInfo.wandaSubscriptionCost,
          renewOn
        ),
      ]);

      TrackAnalytics(
        "/Registration",
        "Registration subscription selection",
        "Registration Plan Modal"
      );
    } catch (error) {
      // We are already showing an error on the form. so removed toast notification.
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCCPRegistrationDetails = async () => {
    try {
      const statusUpdateData = {
        CCPIntake: true,
        insertParameter: InsertParameter.CCPIntake,
        registerFor: whoWillUseServices,
        personId: caregiverSummaryInfo.personId,
        registrationComplete: true,
      };
      await UpdateCCPRegistrationDetails(statusUpdateData);
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Failed to update registration status details. Please try again later.",
      });
    }
  };

  return (
    <div className={styles.subscriptionPlanContainer}>
      <Row>
        <Col xs={{ offset: 2 }} lg={{ offset: 4 }}>
          <CustomButton
            text="Back"
            handlerFunction={handleBack}
            type="link"
            className="backBtn"
            icon={backArrowIcon}
          />
        </Col>
      </Row>

      {!upgradePlan ? (
        <>
          <Row justify="center">
            <Col span={24} className={styles.subscriptionTitle}>
              Select a subscription
            </Col>
          </Row>
          <Row justify="center" gutter={[50, 20]}>
            <Col xs={24} lg={12} id={styles.basicPlanCardColumn}>
              <PlanCard
                plan="Basic"
                selectedPlan={selectedPlan}
                handleCardClick={handleCardClick}
                wandaPlusDiscount={tripDetailsInfo.wandaPlusDiscount}
                wandaSubscriptionCost={tripDetailsInfo.wandaSubscriptionCost}
              />
            </Col>
            <Col xs={24} lg={12} id={styles.plusPlanCardColumn}>
              <PlanCard
                plan="Plus"
                selectedPlan={selectedPlan}
                handleCardClick={handleCardClick}
                wandaPlusDiscount={tripDetailsInfo.wandaPlusDiscount}
                wandaSubscriptionCost={tripDetailsInfo.wandaSubscriptionCost}
              />
            </Col>
          </Row>
          <Row justify="center" className={styles.continueBtnContainer}>
            <Col>
              <Button
                type="primary"
                id={styles.continueBtn}
                onClick={handleContinue}
                className={
                  selectedPlan === "" ? styles.subscriptionContinueBtn : ""
                }
                disabled={selectedPlan === "" ? true : false}
              >
                Continue
              </Button>
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Row justify="center">
            <Col span={24} className={styles.upgradePlanTitle}>
              Upgrade to Wanda Plus
            </Col>
            <Col span={24} className={styles.upgradePlanDescription}>
              ${tripDetailsInfo.wandaSubscriptionCost}/month
            </Col>
            <Col span={24}>
              <CreditCardForm
                form={form}
                isValidCCState={isValidCCState}
                validateAndCheckForm={validateAndCheckForm}
              />
            </Col>
            <Col span={24}>
              <Row justify="center">
                <Col>
                  <Button
                    className={`${
                      styles.registrationCreditFormUpgradeBtn
                    } ${isLoading && styles.autoWidth} ${shouldDisableButton &&
                      styles.disableBtnOpacity}`}
                    onClick={handleUpgradePlus}
                    disabled={shouldDisableButton}
                    loading={isLoading}
                  >
                    Upgrade to Plus
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default WandaPlusSubscription;
